import { DEFAULT_DEPOSIT_SLIPPAGE } from "../../constants";
import { WAD } from "../../utils/bigint";

/**
 * Calculates the minimum amount of tokens to mint based on deposit amount, rate, and slippage
 * @param depositAmount The amount being deposited (in base units)
 * @param rate The current exchange rate between deposit and mint tokens
 * @param vaultTokenDecimals The number of precision decimals of the vault token
 * @param slippage The maximum acceptable slippage as a decimal (e.g., 0.01 for 1%)
 * @returns The minimum amount of tokens that should be minted accounting for slippage in vault token precision decimals
 */
const calculateMinimumMint = (
  depositAmount: bigint, // In deposit token decimals
  rate: bigint, // In deposit token decimals
  vaultTokenDecimals: number, // The number of precision decimals of the vault token
  slippage?: number // In bps (e.g., 50 for 0.5%) = 50 / 10000 = 0.005 = 0.5%
): bigint => {
  const slippageValue = slippage ?? DEFAULT_DEPOSIT_SLIPPAGE;
  // Convert bps to WAD format (multiply by WAD/10000)
  const slippageAsBigInt = (BigInt(slippageValue) * WAD.bigint) / BigInt(10000);

  // Calculate ideal mint amount without slippage
  const minimumMint = (depositAmount * WAD.bigint) / rate;

  // Calculate the amount to subtract for slippage tolerance
  const slippageAmount = (minimumMint * slippageAsBigInt) / WAD.bigint;

  // Return mint amount minus slippage buffer
  if (vaultTokenDecimals > 18) {
    return (
      (minimumMint - slippageAmount) *
      BigInt(10) ** (BigInt(vaultTokenDecimals) - BigInt(18))
    );
  }
  return (
    (minimumMint - slippageAmount) /
    BigInt(10) ** (BigInt(18) - BigInt(vaultTokenDecimals))
  );
};

export { calculateMinimumMint };
